import type { Brand, BrandAssetKind, BrandColorItem } from '@models/brand.model';
import { assetDisplayName, brandLogoImages } from '@models/brandVariants';
import { ASSET_KIND_LABELS } from '@/modules/organizations/hooks/brandAssetEntries';
import type { AssetKit, AssetKitAsset, AssetKitFont, AssetKitTypographyStyle } from '@/modules/kinema/assetKit';

// The Brand tab lists the kinds in the Branding tab's order.
const ASSET_KIND_ORDER: BrandAssetKind[] = ['LOGO', 'FAVICON', 'ICON', 'GRAPHIC_PATTERN', 'LIBRARY'];

// The legacy color slots go by the names the Branding tab gives them.
const LEGACY_COLOR_NAMES: Record<string, string> = {
  'legacy:primary': 'Primary',
  'legacy:secondary': 'Secondary',
  'legacy:tertiary': 'Accent',
};

function brandFontsToAssetKitFonts(brand: Brand | null): AssetKitFont[] {
  if (!brand) return [];
  return (brand.fonts ?? [])
    .map((fv) => ({
      family: fv.family_name,
      weight: fv.weight,
      italic: fv.italic,
      url: fv.file_url ?? fv.source ?? '',
    }))
    .filter((f) => f.url);
}

function legacyColors(brand: Brand): AssetKit['colors'] {
  const colors: AssetKit['colors'] = [];
  if (brand.primary_color_code) colors.push({ name: 'Primary', value: brand.primary_color_code });
  if (brand.secondary_color_code) colors.push({ name: 'Secondary', value: brand.secondary_color_code });
  if (brand.tertiary_color_code) colors.push({ name: 'Tertiary', value: brand.tertiary_color_code });
  return colors;
}

/**
 * Every color saved on the brand's unified color list (MPN-2031). A legacy slot still at its
 * column default was never set, and a value that was never #RRGGBB can't be painted, so
 * neither is a swatch.
 */
function unifiedColors(items: BrandColorItem[]): AssetKit['colors'] {
  return items
    .filter((c) => c.is_valid_hex && !c.is_default)
    .map((c) => ({ name: c.name || LEGACY_COLOR_NAMES[c.id], value: c.hex_code }));
}

function brandAssets(brand: Brand): AssetKitAsset[] {
  return ASSET_KIND_ORDER.flatMap((kind) =>
    (brand.assets ?? [])
      .filter((asset) => asset.kind === kind && asset.url)
      .map((asset) => ({
        name: asset.name || assetDisplayName(asset.tag, asset.url),
        url: asset.url as string,
        mediaType: asset.media_type,
        group: ASSET_KIND_LABELS[kind],
      })),
  );
}

function brandTypography(brand: Brand): AssetKitTypographyStyle[] {
  return (brand.typography_styles ?? []).map((style) => ({
    name: style.name,
    category: style.category,
    family: style.family_name,
    source: style.source,
  }));
}

/**
 * The editor's AssetKit. `colors` is the brand's unified color list once brand-identity-v2
 * has read it (useBrandColors); null keeps the legacy color columns. `typography` is whether
 * brand-identity-v2 is on; the brand carries its typography styles either way.
 */
export function brandToAssetKit(
  brand: Brand | null,
  colors: BrandColorItem[] | null = null,
  { typography = false }: { typography?: boolean } = {},
): AssetKit {
  if (!brand) return { colors: [], fonts: [], images: [] };
  return {
    colors: colors ? unifiedColors(colors) : legacyColors(brand),
    fonts: brandFontsToAssetKitFonts(brand),
    images: brandLogoImages(brand),
    assets: brandAssets(brand),
    ...(typography ? { typography: brandTypography(brand) } : {}),
  };
}
